import React from 'react';
import { MultiSelect, Select, TextInput } from 'react-hook-form-mantine';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	useTranslationCapManage,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

import { Flex, Tooltip } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';

import { NATURAL_PERSON_FORM_FIELDS, useDetailsSection } from './useDetailsSection';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { PhoneNumberInput } from '@dealstack/frontend/shared/components/forms/PhoneNumberInput/PhoneNumberInput.tsx';
import { Control, FormProvider } from 'react-hook-form';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import {
	COUNTRIES_NAME_CODE_GROUPED_OPTIONS,
	getCountryNameByCode,
} from '@dealstack/shared/lib/countries/countriesData.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';
import { groupedSelectDividerStyles } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { BasicDetailsHistoryLink } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/BasicDetailsHistoryLink/BasicDetailsHistoryLink.tsx';

const CountryOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_COUNTRY_OPTION);

interface Props {
	hasEditorPermission: boolean;
	hasSupportPermission: boolean;
}

export const DetailsSection = ({ hasEditorPermission, hasSupportPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const tDefault = useTranslationDefault();
	const [editMode, setEditMode] = React.useState(false);

	const {
		naturalPersonDetailsForm,
		onNaturalPersonFormSubmit,
		isLoading,
		selectedEntityData,
		canEditEmail,
		isLinkedToUser,
	} = useDetailsSection(tCapManage, tDefault, hasSupportPermission, hasEditorPermission);

	const emailLabel = isLinkedToUser ? (
		<Flex align="center" gap="0.25rem">
			{tCapManage('basic_details.natural_person_form.email_label')}
			<Tooltip
				withinPortal
				label={tCapManage('basic_details.natural_person_form.email_linked_tooltip')}
			>
				<Flex data-testid={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_EMAIL_LINKED_INDICATOR}>
					<Icon variant="link-chain-light" size="sm" />
				</Flex>
			</Tooltip>
		</Flex>
	) : (
		tCapManage('basic_details.natural_person_form.email_label')
	);

	return (
		<EditableInfoContainer
			title={tCapManage('basic_details.natural_person_form.basic_details')}
			isEdit={editMode}
			setIsEdit={setEditMode}
			disableButtons={isLoading}
			onUpdate={naturalPersonDetailsForm.handleSubmit(onNaturalPersonFormSubmit)}
			onCancel={() => setEditMode(false)}
			hideRemove={true}
			hasEditorPermission={hasEditorPermission}
			skipTopBorder={true}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_EDIT_BUTTON,
				'BASIC_DETAILS'
			)}
			updateBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_UPDATE_BUTTON,
				'BASIC_DETAILS'
			)}
		>
			<Flex direction="column" w="100%">
				<FormProvider {...naturalPersonDetailsForm}>
					<EditableInfoContainerGrid $gridColumns={2}>
						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.natural_person_form.first_name_label')}
							data={selectedEntityData?.firstName || LONG_DASH}
							formInput={
								<TextInput
									name={NATURAL_PERSON_FORM_FIELDS.first_name}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.first_name_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.natural_person_form.last_name_label')}
							data={selectedEntityData?.lastName}
							formInput={
								<TextInput
									name={NATURAL_PERSON_FORM_FIELDS.last_name || LONG_DASH}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.last_name_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage(
								'basic_details.natural_person_form.date_of_birth_label'
							)}
							data={formatBEDateToUiWithFallback(
								selectedEntityData?.dateOfBirth,
								DATE_FORMATS.UI,
								LONG_DASH
							)}
							formInput={
								<MantineDateInput
									name={NATURAL_PERSON_FORM_FIELDS.date_of_birth}
									maxDate={new Date()}
									placeholder={tCapManage(
										'basic_details.natural_person_form.date_of_birth_placeholder'
									)}
									disabled={!hasEditorPermission}
									size="md"
									data-testid={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_DOB_INPUT}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage(
								'basic_details.natural_person_form.country_of_birth_label'
							)}
							data={
								getCountryNameByCode(selectedEntityData?.countryOfBirth) ??
								LONG_DASH
							}
							formInput={
								<Select
									name={NATURAL_PERSON_FORM_FIELDS.country_of_birth}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.country_of_birth_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
									data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
									allowDeselect
									data-testid={
										TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_COUNTRY_OF_BIRTH_SELECT
									}
									renderOption={CountryOption}
									styles={groupedSelectDividerStyles}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage(
								'basic_details.natural_person_form.place_of_birth_label'
							)}
							data={selectedEntityData?.placeOfBirth || LONG_DASH}
							formInput={
								<TextInput
									name={NATURAL_PERSON_FORM_FIELDS.place_of_birth}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.place_of_birth_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
									data-testid={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_POB_INPUT}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage(
								'basic_details.natural_person_form.citizenship_label'
							)}
							data={
								(selectedEntityData?.citizenship || [])
									.map((c) => getCountryNameByCode(c))
									.join(', ') || LONG_DASH
							}
							formInput={
								<MultiSelect
									name={NATURAL_PERSON_FORM_FIELDS.citizenship}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.citizenship_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
									data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
									data-testid={
										TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_CITIZENSHIP_MULTISELECT
									}
									renderOption={CountryOption}
									styles={groupedSelectDividerStyles}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.natural_person_form.domicile_label')}
							data={getCountryNameByCode(selectedEntityData?.domicile) ?? LONG_DASH}
							formInput={
								<Select
									name={NATURAL_PERSON_FORM_FIELDS.domicile}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.domicile_placeholder'
									)}
									size="md"
									disabled={!hasEditorPermission}
									data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
									allowDeselect
									data-testid={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_DOMICILE_SELECT}
									renderOption={CountryOption}
									styles={groupedSelectDividerStyles}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={emailLabel}
							data={selectedEntityData?.email || LONG_DASH}
							valueDataTestId={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_EMAIL_VALUE}
							formInput={
								<TextInput
									name={NATURAL_PERSON_FORM_FIELDS.email}
									control={naturalPersonDetailsForm.control}
									placeholder={tCapManage(
										'basic_details.natural_person_form.email_placeholder'
									)}
									size="md"
									disabled={!canEditEmail}
									data-testid={TEST_ELEMENT_IDS.CAP_BASIC_DETAILS_EMAIL_INPUT}
									onChange={(e) => {
										naturalPersonDetailsForm.setValue(
											NATURAL_PERSON_FORM_FIELDS.email,
											e.target.value,
											{
												shouldValidate: true,
												shouldDirty: true,
											}
										);
									}}
								/>
							}
						/>

						<EditableInfoField
							editMode={editMode}
							label={tCapManage('basic_details.natural_person_form.phone_label')}
							allowOverflow={true}
							data={
								selectedEntityData?.phoneNumber
									? `${selectedEntityData?.telephonePrefix ?? ''}${selectedEntityData?.phoneNumber}`
									: LONG_DASH
							}
							formInput={
								<Flex mt="0.25rem" w="100%" style={{ flex: 1 }}>
									<PhoneNumberInput
										name={NATURAL_PERSON_FORM_FIELDS.phone_number}
										phonePrefixName={
											NATURAL_PERSON_FORM_FIELDS.telephone_prefix
										}
										control={
											naturalPersonDetailsForm.control as unknown as Control
										}
										size="m"
										disabled={!hasEditorPermission}
									/>
								</Flex>
							}
						/>
					</EditableInfoContainerGrid>
				</FormProvider>

				{!editMode && <BasicDetailsHistoryLink />}
			</Flex>
		</EditableInfoContainer>
	);
};
